import { useOutlet } from 'react-router-dom'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { get } from 'lodash/fp'
import { IframeAppContext } from '../../../../../features/remote-app/RemoteApp/IframeApp'

type CacheEntry = { key: string; element: ReactElement }

export const KeepAliveOutlet = ({ limit = 8 }: { limit?: number }) => {
  const outlet = useOutlet()
  const pageName = get(
    'props.children.props.match.route.element.props.name',
    outlet,
  ) as string

  const routeKey = useMemo(() => pageName, [pageName])
  const cachePagesRef = useRef<Map<string, CacheEntry>>(new Map())
  const [, forceUpdate] = useState(0)

  useEffect(() => {
    if (!outlet) return
    if (!cachePagesRef.current.has(routeKey)) {
      if (cachePagesRef.current.size >= limit) {
        const firstKey = cachePagesRef.current.keys().next().value as string
        cachePagesRef.current.delete(firstKey)
      }

      cachePagesRef.current.set(routeKey, {
        key: routeKey,
        element: outlet,
      })
      forceUpdate((x) => x + 1)
    }
  }, [routeKey, limit])

  return (
    <>
      {[...cachePagesRef.current.values()].map(({ key, element }) => (
        <div
          key={key}
          data-name={key}
          data-testid="keep-alive-outlet"
          style={{ display: key === routeKey ? 'block' : 'none' }}
          className="h-full w-full"
        >
          <IframeAppContext.Provider
            value={{
              isActive: key === routeKey,
            }}
          >
            {element}
          </IframeAppContext.Provider>
        </div>
      ))}
    </>
  )
}
